import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import useBranches from "hooks/useBranches";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { API_BASE } from "config/api";

const ACTION_COLOR = { created: "success", updated: "info", deleted: "error" };

const ActionCell = ({ value }) => (
  <Chip
    label={value}
    color={ACTION_COLOR[value] || "default"}
    size="small"
    sx={{ textTransform: "capitalize", fontWeight: 600 }}
  />
);
ActionCell.propTypes = { value: PropTypes.string.isRequired };

const WhenCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary">
    {value ? new Date(value).toLocaleString() : "—"}
  </Typography>
);
WhenCell.propTypes = { value: PropTypes.string };

const DescriptionCell = ({ value, row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {value}
    </Typography>
    {row.original.branchName && (
      <Typography variant="caption" color="text.secondary">
        {row.original.branchName}
      </Typography>
    )}
  </Box>
);
DescriptionCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
};

export default function useAuditLogData() {
  const { church } = useAuth();
  const { branches, canFilterByBranch } = useBranches();

  const [branchFilter, setBranchFilter] = useState(null);
  const [actionFilter, setActionFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [types, setTypes] = useState([]);

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  const columns = [
    { Header: "When", accessor: "whenAt", align: "left", width: "15%", Cell: WhenCell },
    { Header: "User", accessor: "userName", align: "left", width: "15%" },
    { Header: "Action", accessor: "action", align: "center", width: "10%", Cell: ActionCell },
    {
      Header: "Description",
      accessor: "description",
      align: "left",
      width: "40%",
      Cell: DescriptionCell,
    },
    { Header: "Type", accessor: "type", align: "center", width: "10%" },
  ];

  const fetchTypes = useCallback(async () => {
    if (!church?.id) return;
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE}/audit-logs/types`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setTypes(data.data || []);
    } catch (err) {
      console.error("Failed to fetch audit log types:", err);
    }
  }, [church?.id]);

  const fetchLogs = useCallback(async () => {
    if (!church?.id) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams({
        page: String(page + 1),
        per_page: String(rowsPerPage),
      });
      if (branchFilter) params.set("branch_id", branchFilter);
      if (actionFilter) params.set("action", actionFilter);
      if (typeFilter) params.set("type", typeFilter);

      const response = await fetch(`${API_BASE}/audit-logs?${params.toString()}`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const logs = result.data || [];
      const branchById = Object.fromEntries(branches.map((b) => [b.id, b.name]));

      setRows(
        logs.map((log) => ({
          id: log.id,
          whenAt: log.created_at,
          userName: log.user?.name || "System",
          action: log.action,
          description: log.description,
          type: log.auditable_type,
          branchName: log.branch_id ? branchById[log.branch_id] : null,
        }))
      );
      setTotal(result.meta?.total || logs.length);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch audit logs:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [church?.id, page, rowsPerPage, branchFilter, actionFilter, typeFilter, branches]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return {
    columns,
    rows,
    isLoading,
    error,
    branches,
    canFilterByBranch,
    branchFilter,
    setBranchFilter: (v) => {
      setPage(0);
      setBranchFilter(v);
    },
    actionFilter,
    setActionFilter: (v) => {
      setPage(0);
      setActionFilter(v);
    },
    typeFilter,
    setTypeFilter: (v) => {
      setPage(0);
      setTypeFilter(v);
    },
    types,
    serverPagination: {
      page,
      rowsPerPage,
      count: total,
      onPageChange: setPage,
      onRowsPerPageChange: (n) => {
        setPage(0);
        setRowsPerPage(n);
      },
    },
  };
}
